import { useEffect, useState } from "react";
import axios from "axios";
import {
  Briefcase,
  Mail,
  Phone,
  Building2,
  Filter,
  Download,
  Trash2,
  X,
  RefreshCw,
  Inbox,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/applications`);
      setApps(data);
    } catch (e) {
      toast.error("Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const downloadResume = (app) => {
    // Open in a new tab - axios.defaults.withCredentials = true ensures session cookies are sent
    window.open(`${API}/admin/applications/${app.id}/resume`, "_blank");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`);
      setApps((prev) => prev.filter((a) => a.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Application deleted.");
    } catch (e) {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid="admin-applications-page" className="p-6 lg:p-10">
      <Toaster position="top-right" richColors />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Job Applications</h1>
          <p className="mt-1 text-sm text-slate-500">Resumes and applications from the careers page.</p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="apps-refresh"
          className="border-slate-300 text-slate-700"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading applications...</div>
        ) : apps.length === 0 ? (
          <div className="py-16 text-center">
            <Briefcase className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No applications yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {apps.map((app) => (
              <li
                key={app.id}
                data-testid={`app-row-${app.id}`}
                onClick={() => setSelected(app)}
                className={`cursor-pointer px-5 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4 ${
                  selected?.id === app.id ? "bg-sky-50/40" : ""
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-bold shrink-0">
                  {app.first_name?.charAt(0)}{app.last_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 truncate">{app.first_name} {app.last_name}</p>
                    <p className="text-xs text-slate-500 truncate">{app.email}</p>
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm text-slate-700 truncate">{app.position}</p>
                    <p className="text-xs text-slate-400 truncate">{app.company || "-"}</p>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs text-slate-400 hidden md:inline">{formatDate(app.created_at)}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); downloadResume(app); }}
                      data-testid={`app-resume-${app.id}`}
                      className="rounded-full text-xs h-8 border-sky-200 text-sky-700 hover:bg-sky-50"
                    >
                      <Download className="h-3 w-3 mr-1" /> Resume
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <aside className="ml-auto relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Application</p>
                <h2 className="text-lg font-bold text-slate-900">{selected.first_name} {selected.last_name}</h2>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                data-testid="app-drawer-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <p className="text-[10px] font-bold text-sky-700 uppercase tracking-widest">Position</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{selected.position}</p>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <a href={`mailto:${selected.email}`} className="text-sky-600 hover:underline break-all">{selected.email}</a>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <a href={`tel:${selected.phone}`} className="text-slate-700">{selected.phone}</a>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-700">{selected.company || "-"}</span>
                </div>
                <div className="flex items-start gap-3">
                  <Filter className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-700">Heard via: <span className="font-medium">{selected.referral_source}</span></span>
                </div>
              </div>
              {selected.other_info && (
                <div className="border-t border-slate-100 pt-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Cover note</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-lg p-3">{selected.other_info}</p>
                </div>
              )}
              <div className="border-t border-slate-100 pt-5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Resume</p>
                <Button
                  onClick={() => downloadResume(selected)}
                  data-testid="app-detail-resume"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                >
                  <FileText className="h-4 w-4 mr-2" /> View / Download {selected.resume_filename}
                </Button>
                <p className="mt-2 text-xs text-slate-400">{(selected.resume_size / 1024).toFixed(0)} KB</p>
              </div>
              <div className="border-t border-slate-100 pt-5">
                <Button
                  variant="outline"
                  onClick={() => remove(selected.id)}
                  data-testid="app-delete"
                  className="w-full border-red-200 text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete application
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
